/**
 * Dot-path helpers for the `v1/output/…` field registry.
 *
 * Deliberately tiny and dependency-free: these run inside the MQTT worker
 * on every message, so they must stay allocation-light.
 */

/** Read `a.b.c` out of an arbitrary parsed JSON value. Returns undefined
 *  for any missing segment — never throws on a malformed payload. */
export function getPath(obj: unknown, path: string): unknown {
  if (obj == null) return undefined;
  // Fast path: no dot means a plain top-level key.
  if (!path.includes('.')) {
    return typeof obj === 'object' ? (obj as Record<string, unknown>)[path] : undefined;
  }
  let cur: unknown = obj;
  for (const seg of path.split('.')) {
    if (cur == null || typeof cur !== 'object') return undefined;
    cur = (cur as Record<string, unknown>)[seg];
  }
  return cur;
}

/**
 * Flatten a payload into the set of dot-paths that actually carry a value.
 *
 * Arrays are recorded as the array path itself (`ext.waypoints`) rather
 * than being walked per-element — the registry maps whole arrays, and
 * walking a 37-point polygon on every message would be wasted work.
 *
 * `maxDepth` guards against a pathological nested payload turning one
 * message into thousands of string allocations.
 */
export function collectPaths(
  obj: unknown,
  prefix = '',
  out = new Set<string>(),
  depth = 0,
): Set<string> {
  if (obj == null || typeof obj !== 'object' || Array.isArray(obj)) return out;
  for (const [k, v] of Object.entries(obj as Record<string, unknown>)) {
    const path = prefix ? `${prefix}.${k}` : k;
    out.add(path);
    // Recurse into plain objects only; arrays are terminal (see above).
    if (v != null && typeof v === 'object' && !Array.isArray(v) && depth < 4) {
      collectPaths(v, path, out, depth + 1);
    }
  }
  return out;
}
